import type { RevisionDTO } from '@conversed/rest/src/contracts';
import type {
  CreateWebclientConfigRequestDto,
  UpdateWebclientConfigRequestDto,
} from '@conversed/shared/src/central/dto/webclient-config.dto';
import { mainActions } from '@store/reducers/mainHandler';
import { useCallback, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  addWebclientWhitelistEndpoint,
  createWebclientConfigEndpoint,
  createWebclientEndpoint,
  deleteWebclientConfigEndpoint,
  getWebclientConfigsEndpoint,
  removeWebclientWhitelistedDomainEndpoint,
  setWebclientConfigDefaultEndpoint,
  setWebclientConfigStatusEndpoint,
  updateWebclientConfigEndpoint,
} from '../../../api/webclientsApi';
import { Actions } from '../../../constants/actionTypes';
import { toastErrorTranslated } from '../../../helpers/toast';
import { findDefaultWebclientConfig } from '../../../helpers/webclientHelper';
import { useMainDispatch } from '../../useMainDispatch';

export const useWebclientActions = () => {
  const dispatch = useMainDispatch();
  const newDispatch = useDispatch();
  const isFetchingRef = useRef(false);

  const fetchWebclientConfigs = useCallback(async () => {
    // Prevent concurrent calls
    if (isFetchingRef.current) {
      return;
    }

    try {
      isFetchingRef.current = true;
      dispatch({
        type: Actions.SET_WEBCLIENT,
        payload: { isLoadingConfigs: true },
      });
      const { data: webclientConfigs } = await getWebclientConfigsEndpoint();

      // * Derive the default from the freshly-fetched data so that a page reload always picks the
      // * correct default rather than the pre-fetch Redux state, which is empty on a full reload.
      const defaultFetchedConfig = findDefaultWebclientConfig(
        webclientConfigs.configs,
      );

      dispatch({
        type: Actions.SET_WEBCLIENT,
        payload: {
          configs: webclientConfigs.configs,
          uuid: webclientConfigs.uuid,
          botId: webclientConfigs?.botId,
          environment: webclientConfigs?.environment,
          whitelistedDomains: webclientConfigs?.whitelistedDomains,
          selectedConfigId: defaultFetchedConfig?.identifier,
          isEnabledWebclientAccess: webclientConfigs?.isEnabledWebclientAccess,
          isLoadingConfigs: false,
        },
      });

      if (webclientConfigs.menuEntries) {
        dispatch({
          type: Actions.SET_DESIGNER,
          payload: { menuEntries: webclientConfigs.menuEntries },
        });
      }

      if (webclientConfigs.revisions) {
        newDispatch(
          mainActions.populateRevision(
            webclientConfigs.revisions as RevisionDTO[],
          ),
        );
      }

      // voiceAgents is present (even as []) only when the WebclientVoice platform feature flag is enabled for the company
      if (webclientConfigs.voiceAgents !== undefined) {
        newDispatch(
          mainActions.setVoiceAIAgents({
            agents: webclientConfigs.voiceAgents,
          }),
        );
      }
    } catch (error: any) {
      if (error?.response?.data?.errorCode !== 'NOT_FOUND') {
        toastErrorTranslated(error);
      }
      dispatch({
        type: Actions.SET_WEBCLIENT,
        payload: {
          configs: [],
          uuid: undefined,
          botId: undefined,
          environment: undefined,
          whitelistedDomains: undefined,
          isLoadingConfigs: false,
        },
      });
    } finally {
      isFetchingRef.current = false;
    }
  }, [dispatch, newDispatch]);

  const createWebclient = useCallback(async () => {
    try {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
      await createWebclientEndpoint();
      await fetchWebclientConfigs();
    } catch (error: any) {
      console.error(error);
      toastErrorTranslated(error);
    } finally {
      dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
    }
  }, [dispatch, fetchWebclientConfigs]);

  const createWebclientConfig = useCallback(
    async (name: string, config: CreateWebclientConfigRequestDto['config']) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await createWebclientConfigEndpoint(name, config);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const updateWebclientConfig = useCallback(
    async (
      identifier: string,
      name: string,
      config: UpdateWebclientConfigRequestDto['config'],
    ) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await updateWebclientConfigEndpoint(identifier, name, config);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const deleteWebclientConfig = useCallback(
    async (identifier: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await deleteWebclientConfigEndpoint(identifier);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const addWhitelistedDomain = useCallback(
    async (domain: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await addWebclientWhitelistEndpoint(domain);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );
  const removeWebclientWhitelistedDomain = useCallback(
    async (whitelistedDomain: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await removeWebclientWhitelistedDomainEndpoint(whitelistedDomain);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const setWebclientConfigStatus = useCallback(
    async (id: string, status: boolean) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await setWebclientConfigStatusEndpoint(id, status);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  const setWebclientConfigDefault = useCallback(
    async (id: string) => {
      try {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: true } });
        await setWebclientConfigDefaultEndpoint(id);
        await fetchWebclientConfigs();
      } catch (error: any) {
        console.error(error);
        toastErrorTranslated(error);
      } finally {
        dispatch({ type: Actions.SET_UI, payload: { isLoading: false } });
      }
    },
    [dispatch, fetchWebclientConfigs],
  );

  return {
    fetchWebclientConfigs,
    createWebclientConfig,
    createWebclient,
    updateWebclientConfig,
    deleteWebclientConfig,
    addWhitelistedDomain,
    removeWebclientWhitelistedDomain,
    setWebclientConfigStatus,
    setWebclientConfigDefault,
  };
};
